import { useMemo, useState } from "react";
import { formatUnits } from "viem";
import type { EstadoVault } from "../hooks/useVault";
import { num } from "../lib/formato";
import { useT } from "../lib/i18n";

/**
 * GANANCIAS — lo que ha generado la posición, TODO: cobrado y aún sin cobrar. El número grande es el
 * total generado (es ganancia igual, esté cobrada o no); debajo, DÓNDE está cada parte ahora mismo y
 * un gráfico de lo cobrado por día / semana / mes.
 *
 * El reparto se lee del ESTADO actual + los eventos, no de una suma frágil:
 *   · Apartado para ti — saldo `profit0/1` del contrato: lo retirable AHORA (0 si lo reinvertiste todo).
 *   · Reinvertido      — lo cobrado NETO menos lo apartado ahora menos lo retirado: la parte que se
 *                        generó y quedó TRABAJANDO en la posición (auto o reinvertida a mano). No se
 *                        suma `ProfitReinvested` a pelo: al reinvertir, ese dinero gana más comisiones
 *                        que se reinvierten otra vez, y contarlas infla el mismo dólar varias veces.
 *   · Retirado         — `FeesWithdrawn`: lo que ya se fue a tu wallet.
 *   · Comisión         — `PerfFeeTaken`: el 10% de lo generado.
 *   · Sin cobrar       — lo que Uniswap retiene, simulado sobre el NFT.
 * El total generado es la SUMA de esas partes, así que el número grande y el reparto siempre casan
 * (y coinciden con la tarjeta del Resumen: apartado+reinvertido+retirado = lo cobrado neto).
 *
 * TODO SE VALORA EN LA MONEDA DE REFERENCIA al precio de AHORA, igual que el resto de la pantalla.
 */

/** Comisión de Keellabs (todas las factories: 1000 bps, verificado 2026-11-04). */
const BPS = 1000;

type Log = { eventName: string; blockNumber: bigint; args: Record<string, unknown> };
type Grano = "dia" | "semana" | "mes";
type Estilo = "barras" | "area";

export function PanelComisiones({
  v, logs, fechas,
}: {
  v: EstadoVault;
  logs: readonly Log[];
  fechas: Map<bigint, number>;
}) {
  const t = useT();
  const [grano, setGrano] = useState<Grano>("dia");
  const [estilo, setEstilo] = useState<Estilo>("barras");

  const datos = useMemo(() => {
    const t = (x: unknown, dec: number) => Number(formatUnits((x as bigint) ?? 0n, dec));
    const enRef = (a0: number, a1: number) =>
      v.refEsToken0 ? a0 + (v.precio > 0 ? a1 / v.precio : 0) : a1 + a0 * v.precio;
    const val = (a: unknown, b: unknown) => enRef(t(a, v.token0.decimals), t(b, v.token1.decimals));

    let netoCobrado = 0, keellabs = 0, gas = 0, retirado = 0;
    let sinFecha = 0, cobros = 0;
    // Tramos del gráfico: comisiones COBRADAS (netas + comisión) por período. Un movimiento posterior
    // (reinversión manual) no es una generación, así que no entra aquí.
    const porTramo = new Map<string, { v: number; ts: number }>();

    const clave = (ts: number) => {
      const d = new Date(ts * 1000);
      if (grano === "mes") return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (grano === "semana") {
        const l = new Date(d);
        l.setDate(d.getDate() - ((d.getDay() + 6) % 7));
        return `${l.getFullYear()}-${String(l.getMonth() + 1).padStart(2, "0")}-${String(l.getDate()).padStart(2, "0")}`;
      }
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };
    const aTramo = (ts: number | undefined, x: number) => {
      if (!ts) { sinFecha++; return; }
      const k = clave(ts);
      const acc = porTramo.get(k) ?? { v: 0, ts };
      acc.v += x; acc.ts = Math.min(acc.ts, ts);
      porTramo.set(k, acc);
    };

    for (const l of logs) {
      const ts = fechas.get(l.blockNumber);
      // Auditoría 2026-11-04: `FeesCollected` solo sale en el cobro a demanda; los cobros de cada
      // reajuste y de `removeLiquidity` solo emiten `PerfFeeTaken` (ver useGanancias). El neto y el
      // bruto se derivan de la comisión: neto = fee·(10000−bps)/bps, bruto = fee·10000/bps.
      if (l.eventName === "PerfFeeTaken") {
        const x = val(l.args.fee0, l.args.fee1);
        keellabs += x; netoCobrado += (x * (10000 - BPS)) / BPS;
        cobros++; aTramo(ts, (x * 10000) / BPS);
      } else if (l.eventName === "GasReimbursed") {
        gas += val(l.args.amount0, l.args.amount1);
      } else if (l.eventName === "FeesWithdrawn") {
        retirado += val(l.args.profit0, l.args.profit1);
      }
    }

    const barras = [...porTramo.entries()].sort((a, b) => a[1].ts - b[1].ts).map(([k, x]) => ({ k, ...x }));
    let corr = 0;
    const serie = barras.map((b) => ({ ts: b.ts, acc: (corr += b.v) }));

    // Saldo apartado ACTUAL (retirable), del contrato — no una suma de eventos.
    const apartado = enRef(t(v.profit0, v.token0.decimals), t(v.profit1, v.token1.decimals));
    // Lo cobrado neto (para ti + auto) que NO está apartado ni se retiró: quedó trabajando en la
    // posición. Es la medida honesta de «reinvertido», sin la inflación por composición de sumar
    // los `ProfitReinvested`. Se acota a 0 por si el precio de valoración lo cruza por poco.
    const reinvertido = Math.max(0, netoCobrado - apartado - retirado);
    // Comisiones acumuladas en el NFT pero AÚN SIN COBRAR (bruto, antes del 10%). Es ganancia igual.
    const pendiente = v.refEsToken0
      ? v.pendientes.a0 + (v.precio > 0 ? v.pendientes.a1 / v.precio : 0)
      : v.pendientes.a1 + v.pendientes.a0 * v.precio;

    const generado = netoCobrado + keellabs + pendiente;
    const cobrado = netoCobrado + keellabs;

    return {
      apartado, reinvertido, retirado, keellabs, pendiente, generado, cobrado, gas,
      cobros, sinFecha, barras, serie,
    };
  }, [logs, fechas, grano, v]);

  const u = v.ref.symbol;
  const hayAlgo = datos.generado > 0 || datos.gas > 0;
  const pend = datos.pendiente > 0.005;
  const hayRetiro = datos.retirado > 0.005;

  return (
    <section className="hist-sec">
      <div className="hist-cab"><span>{t("Ganancias")}</span><span className="ln" /></div>

      {!hayAlgo ? (
        <div className="gan"><p className="detalle vacio">{t("La posición todavía no ha generado ninguna comisión.")}</p></div>
      ) : (
        <div className="gan">
          <div className="gan-top">
            <div>
              <div className="lede">{t("Ganancias generadas · total")}</div>
              <div className="big tnum">{num(datos.generado)}<span className="u">{u}</span></div>
              <div className="sub">
                {t("{n} ya cobrado", { n: num(datos.cobrado) })}
                {pend && <> · <b className="pendc">{num(datos.pendiente)}</b> {t("aún sin cobrar en la posición")}</>}
              </div>
            </div>
          </div>

          {datos.barras.length > 0 && (
            <>
              <div className="cab-grafico">
                <div className="toggle chico">
                  {(["dia", "semana", "mes"] as const).map((g) => (
                    <button key={g} className={grano === g ? "on" : ""} onClick={() => setGrano(g)}>
                      {g === "dia" ? t("Día") : g === "semana" ? t("Semana") : t("Mes")}
                    </button>
                  ))}
                </div>
                {datos.serie.length >= 2 && (
                  <div className="toggle chico">
                    <button className={estilo === "barras" ? "on" : ""} onClick={() => setEstilo("barras")}>{t("Barras")}</button>
                    <button className={estilo === "area" ? "on" : ""} onClick={() => setEstilo("area")}>{t("Acumulado")}</button>
                  </div>
                )}
              </div>
              <div className="rotulo-graf">{t("Comisiones cobradas por período")}</div>
              {estilo === "area" && datos.serie.length >= 2
                ? <Area serie={datos.serie} />
                : <Grafico barras={datos.barras} unidad={u} grano={grano} />}
            </>
          )}

          <div className={`reparto ${hayRetiro ? "cinco" : ""}`}>
            <Caja tono="ti" k={t("Apartado para ti")} val={num(datos.apartado)}
              nota={datos.apartado > 0.005 ? t("retirable ahora mismo") : t("nada apartado (lo reinvertiste o retiraste)")}
              fuerte={datos.apartado > 0.005} />
            <Caja tono="re" k={t("Reinvertido")} val={num(datos.reinvertido)} nota={t("volvió a la posición a trabajar")}
              fuerte={datos.reinvertido > 0.005} />
            {hayRetiro && <Caja tono="ret" k={t("Retirado a tu wallet")} val={num(datos.retirado)} nota={t("ya fuera del vault")} />}
            <Caja tono="ke" k={t("Comisión Keellabs")} val={num(datos.keellabs)}
              nota={datos.cobrado > 0 ? t("{pct}% de lo cobrado", { pct: ((datos.keellabs / datos.cobrado) * 100).toFixed(1) }) : undefined} />
            <Caja tono="net" k={t("Sin cobrar aún")} val={pend ? num(datos.pendiente) : "0"} nota={t("acumulado en la posición")} />
          </div>

          <p className="detalle pie">
            {t("Total en {u} al precio de ahora, no al del día de cada cobro. «Apartado» es el saldo retirable de ahora mismo; el gráfico cuenta lo cobrado cuando se cobró.", { u })}
            {datos.sinFecha > 0 && ` ${t("{n} cobro(s) antiguos sin fecha quedan fuera del gráfico.", { n: datos.sinFecha })}`}
          </p>
        </div>
      )}
    </section>
  );
}

function Caja({ k, val, nota, tono, fuerte }: { k: string; val: string; nota?: string; tono: string; fuerte?: boolean }) {
  return (
    <div className="rc">
      <div className="k"><i className={`i-${tono}`} />{k}</div>
      <div className={`val tnum ${fuerte ? "fuerte" : ""}`}>{val}</div>
      {nota && <div className="n">{nota}</div>}
    </div>
  );
}

/** Área acumulada de lo cobrado, SVG sin dependencias. Un punto por día/semana/mes. */
function Area({ serie }: { serie: Array<{ ts: number; acc: number }> }) {
  const t = useT();
  const W = 800, H = 210, PAD_I = 46, PAD_D = 10, PAD_A = 14, PAD_B = 8;
  const max = Math.max(...serie.map((p) => p.acc), 0) || 1;
  const t0 = serie[0]!.ts, t1 = serie[serie.length - 1]!.ts;
  const span = Math.max(1, t1 - t0);
  const x = (ts: number) => PAD_I + (W - PAD_I - PAD_D) * ((ts - t0) / span);
  const y = (acc: number) => PAD_A + (H - PAD_A - PAD_B) * (1 - acc / max);
  const pts = serie.map((p) => `${x(p.ts).toFixed(1)},${y(p.acc).toFixed(1)}`);
  const linea = `M${pts.join(" L")}`;
  const area = `M${x(t0).toFixed(1)},${(H - PAD_B).toFixed(1)} L${pts.join(" L")} L${x(t1).toFixed(1)},${(H - PAD_B).toFixed(1)} Z`;
  const ult = serie[serie.length - 1]!;
  const fechaX = (ts: number) => new Date(ts * 1000).toLocaleDateString("es", { day: "numeric", month: "short" });
  return (
    <div className="grafico-acum">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={t("comisiones cobradas acumuladas")}>
        <defs>
          <linearGradient id="acumFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F2603E" stopOpacity="0.32" />
            <stop offset="100%" stopColor="#F2603E" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={PAD_I} x2={W - PAD_D} y1={y(max * f)} y2={y(max * f)} className="reja" />
            <text x={PAD_I - 8} y={y(max * f) + 3} className="eje" textAnchor="end">{num(max * f, max < 1 ? 3 : 2)}</text>
          </g>
        ))}
        <path d={area} fill="url(#acumFill)" />
        <path d={linea} fill="none" className="curva" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={x(ult.ts)} cy={y(ult.acc)} r={4.5} className="fin" />
      </svg>
      <div className="grafico-x"><span>{fechaX(t0)}</span><span>{fechaX(t1)}</span></div>
    </div>
  );
}

/** Barras de lo cobrado por día / semana / mes. El alto escala sobre el mayor tramo. */
function Grafico({
  barras, unidad, grano,
}: {
  barras: Array<{ k: string; v: number; ts: number }>;
  unidad: string;
  grano: Grano;
}) {
  const t = useT();
  const W = 800, H = 200, PAD_I = 46, PAD_D = 8, PAD_A = 12, PAD_B = 26;
  const max = Math.max(...barras.map((b) => b.v), 0) || 1;
  const ancho = (W - PAD_I - PAD_D) / barras.length;
  const grosor = Math.max(3, Math.min(46, ancho * 0.62));
  const y = (val: number) => PAD_A + (H - PAD_A - PAD_B) * (1 - val / max);
  const etiqueta = (ts: number) => {
    const d = new Date(ts * 1000);
    if (grano === "mes") return d.toLocaleDateString("es", { month: "short", year: "2-digit" });
    return d.toLocaleDateString("es", { day: "numeric", month: "short" });
  };
  const cada = Math.ceil(barras.length / 8);
  return (
    <div className="grafico-comisiones">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={t("comisiones cobradas por período")}>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={PAD_I} x2={W - PAD_D} y1={y(max * f)} y2={y(max * f)} className="reja" />
            <text x={PAD_I - 8} y={y(max * f) + 3} className="eje" textAnchor="end">{num(max * f, max < 1 ? 3 : 2)}</text>
          </g>
        ))}
        {barras.map((b, i) => {
          const cx = PAD_I + ancho * i + ancho / 2;
          const xr = cx - grosor / 2;
          const h = (H - PAD_A - PAD_B) * (b.v / max);
          return (
            <g key={b.k}>
              <title>{`${etiqueta(b.ts)} · ${num(b.v)} ${unidad}`}</title>
              <rect className="ti" x={xr} y={H - PAD_B - h} width={grosor} height={h} rx={2} />
              {i % cada === 0 && <text x={cx} y={H - 8} className="eje" textAnchor="middle">{etiqueta(b.ts)}</text>}
            </g>
          );
        })}
        <line x1={PAD_I} x2={W - PAD_D} y1={H - PAD_B} y2={H - PAD_B} className="base" />
      </svg>
    </div>
  );
}
